import React, { useEffect, useRef, useState, useCallback } from "react";
import {
  EmployeeProfile,
  formatDate3,
  getDefaultUserAvatar,
} from "winslow-core";
import Popover from "@mui/material/Popover";
import "./PopoverEmployeeProfile.css";
import { apiGetEmployeeProfileFromEmail } from "../../services/UserService";
import { useDispatch, useSelector } from "react-redux";
import {
  selectEditingCollection,
  setSelectedMenu,
  setSelectedMenuId,
  selectUser,
  selectMergeConnected,
  selectEmployeeProfileFromCache,
  setEmployeeProfileCache,
} from "../../redux/features/appData";
import { CopyTooltip } from "./CopyTooltip";

import config from "environment";
import { useNavigate } from "react-router-dom";
import { MENUS } from "../config/menus";
import ModalHRISRequired from "../collections/ModalHRISRequired";

export type PopoverEmployeeProfileProps = {
  children: React.ReactNode;
  email: string;
  name: string;
  avatar: string;
};

const PopoverEmployeeProfile = ({
  children,
  email,
  name,
  avatar,
}: PopoverEmployeeProfileProps) => {
  const storeUser = useSelector(selectUser);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const currentCollection = useSelector(selectEditingCollection);
  const mergeConnected = useSelector(selectMergeConnected);
  const cachedProfile = useSelector(selectEmployeeProfileFromCache(email));

  const [employeeProfile, setEmployeeProfile] =
    useState<EmployeeProfile | null>(null);
  const [open, setOpen] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [hasLoadedData, setHasLoadedData] = useState<boolean>(false);

  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [showMini, setShowMini] = useState<boolean>(true);

  const triggerRef = useRef<HTMLDivElement>(null);
  const popoverRef = useRef<HTMLDivElement>(null);
  const closeTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const openTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // State for dynamic positioning
  const [anchorOrigin, setAnchorOrigin] = useState<{
    vertical: "top" | "bottom";
    horizontal: "left" | "right";
  }>({
    vertical: "bottom",
    horizontal: "left",
  });

  const [transformOrigin, setTransformOrigin] = useState<{
    vertical: "top" | "bottom";
    horizontal: "left" | "right";
  }>({
    vertical: "top",
    horizontal: "left",
  });

  const [showHRISModal, setShowHRISModal] = useState(false);

  // Memoized function to load employee profile data
  const loadEmployeeProfile = useCallback(async () => {
    if (
      !storeUser?.currentWorkspaceId ||
      !email ||
      hasLoadedData ||
      isLoading
    ) {
      return;
    }

    // Check cache first
    if (cachedProfile) {
      console.log("Using cached employee profile for:", email);
      setEmployeeProfile(cachedProfile);
      if (cachedProfile.mergeId) {
        setShowMini(false);
      }
      setHasLoadedData(true);
      return;
    }

    setIsLoading(true);

    try {
      const result = await apiGetEmployeeProfileFromEmail(
        storeUser.currentWorkspaceId,
        email
      );

      if (result) {
        setEmployeeProfile(result);
        // Cache the result
        dispatch(setEmployeeProfileCache({ email, profile: result }));
        if (result.mergeId) {
          setShowMini(false);
        }
      } else {
        // Create a basic profile if no data found
        const basicProfile = {
          id: "",
          displayName: name,
          email,
          avatar: avatar,
        } as EmployeeProfile;
        setEmployeeProfile(basicProfile);
        // Cache the basic profile to avoid future API calls
        dispatch(setEmployeeProfileCache({ email, profile: basicProfile }));
      }

      setHasLoadedData(true);
    } catch (error) {
      console.error("Failed to load employee profile:", error);
      // Create a basic profile on error
      const basicProfile = {
        id: "",
        displayName: name,
        email,
        avatar: avatar,
      } as EmployeeProfile;
      setEmployeeProfile(basicProfile);
      // Cache the basic profile to avoid future API calls
      dispatch(setEmployeeProfileCache({ email, profile: basicProfile }));
      setHasLoadedData(true);
    } finally {
      setIsLoading(false);
    }
  }, [
    storeUser?.currentWorkspaceId,
    email,
    name,
    avatar,
    hasLoadedData,
    isLoading,
    cachedProfile,
    dispatch,
  ]);

  const clearCloseTimeout = () => {
    if (closeTimeoutRef.current) {
      clearTimeout(closeTimeoutRef.current);
      closeTimeoutRef.current = null;
    }
  };

  const clearOpenTimeout = () => {
    if (openTimeoutRef.current) {
      clearTimeout(openTimeoutRef.current);
      openTimeoutRef.current = null;
    }
  };

  const handlePopoverOpen = (event: React.MouseEvent<HTMLElement>) => {
    event.preventDefault();
    clearCloseTimeout();
    clearOpenTimeout();

    if (open) return;

    const anchor = event.currentTarget;
    openTimeoutRef.current = setTimeout(() => {
      setAnchorEl(anchor);
      setOpen(true);
      // Load data when popover opens
      loadEmployeeProfile();
    }, 1000);
  };

  const handleMouseEnter = () => {
    clearCloseTimeout();
    clearOpenTimeout();
  };

  const handleMouseLeave = () => {
    clearCloseTimeout();
    clearOpenTimeout();

    closeTimeoutRef.current = setTimeout(() => {
      setOpen(false);
    }, 1000);
  };

  const handleTriggerLeave = () => {
    clearOpenTimeout();

    // Only start close timeout if we're not already in the popover
    if (!popoverRef.current?.contains(document.activeElement)) {
      handleMouseLeave();
    }
  };

  useEffect(() => {
    return () => {
      clearCloseTimeout();
      clearOpenTimeout();
    };
  }, []);

  // Create a basic profile for display purposes before data loads
  const displayProfile =
    employeeProfile ||
    ({
      id: "",
      displayName: name,
      email,
      avatar: avatar,
    } as EmployeeProfile);

  return (
    <>
      {open && (
        <div
          className="popover-overlay"
          onClick={(e) => {
            e.stopPropagation();
            setOpen(false);
          }}
        />
      )}

      <div>
        <div
          ref={triggerRef}
          onMouseEnter={handlePopoverOpen}
          onMouseLeave={handleTriggerLeave}>
          {children}
        </div>
        <Popover
          disableRestoreFocus
          open={open}
          id="employee-profile-popover"
          className={showMini ? "employee-profile-popover-mini" : ""}
          anchorEl={anchorEl}
          anchorOrigin={anchorOrigin}
          transformOrigin={transformOrigin}
          onClose={(e) => {
            setOpen(false);
          }}
          onClick={(e) => {
            e.stopPropagation();
          }}
          onMouseEnter={handleMouseEnter}
          onMouseLeave={handleMouseLeave}
          PaperProps={{
            onClick: (e: any) => e.stopPropagation(),
            ref: popoverRef,
            style: { pointerEvents: "auto" },
            onMouseEnter: handleMouseEnter,
            onMouseLeave: handleMouseLeave,
          }}
          style={{ pointerEvents: "none" }}>
          <div id="popover-employee">
            {isLoading ? (
              <div className="popover-employee-loading">
                <div>Loading employee details...</div>
              </div>
            ) : (
              <>
                <div className="popover-employee-hero">
                  <div className="popover-employee-hero-row">
                    <div className="popover-employee-hero-image-container">
                      <div className="popover-employee-hero-image-background">
                        <img
                          className="popover-employee-hero-image"
                          src={
                            displayProfile?.avatar ||
                            avatar ||
                            "/avatar_unknown.png"
                          }
                          alt={getDefaultUserAvatar(
                            displayProfile?.displayName
                          )}
                        />
                      </div>
                    </div>
                    <div className="popover-employee-button-container">
                      {config.showPersonas && hasLoadedData && (
                        <button
                          className="popover-employee-button"
                          disabled={isLoading}
                          onClick={() => {
                            if (!mergeConnected) {
                              setShowHRISModal(true);
                            } else {
                              dispatch(setSelectedMenu(MENUS.COLLECTION_ASK));
                              dispatch(setSelectedMenuId(MENUS.COLLECTION_ASK));
                              navigate(
                                `/collection/${currentCollection?.id}/ask?employeeId=${displayProfile?.id}`
                              );
                            }
                          }}>
                          Ask as <strong>{displayProfile?.displayName}</strong>
                        </button>
                      )}
                    </div>
                  </div>
                  <div className="popover-employee-name">
                    {displayProfile?.displayName || displayProfile?.email}
                  </div>
                  <div className="popover-employee-title">
                    {displayProfile?.title}
                  </div>
                </div>

                {displayProfile?.email && (
                  <CopyTooltip text={displayProfile.email}>
                    <div className="popover-employee-email">
                      <div className="popover-employee-email-content">
                        <img src="/icons/mail.svg" alt="Mail Icon" />
                        <span>{displayProfile.email}</span>
                      </div>
                    </div>
                  </CopyTooltip>
                )}

                <div className="popover-employee-details">
                  <div className="popover-employee-column">
                    <div className="popover-employee-column-title">
                      Basic Details
                    </div>
                    <div className="popover-employee-column-element">
                      <div className="popover-employee-column-element-icon">
                        <img src="/icons/location.svg" alt="Location Icon" />
                      </div>
                      <div className="popover-employee-element-info">
                        <div className="popover-employee-element-info-title">
                          Location
                        </div>
                        <div className="popover-employee-element-info-field">
                          {hasLoadedData ? (
                            displayProfile?.location || (
                              <i className="unknown-value">unknown</i>
                            )
                          ) : (
                            <i className="loading-value">loading...</i>
                          )}
                        </div>
                      </div>
                    </div>
                    <div className="popover-employee-column-element">
                      <div className="popover-employee-column-element-icon">
                        <img src="/icons/country.svg" alt="Country Icon" />
                      </div>
                      <div className="popover-employee-element-info">
                        <div className="popover-employee-element-info-title">
                          Country
                        </div>
                        <div className="popover-employee-element-info-field">
                          {hasLoadedData ? (
                            displayProfile?.country || (
                              <i className="unknown-value">unknown</i>
                            )
                          ) : (
                            <i className="loading-value">loading...</i>
                          )}
                        </div>
                      </div>
                    </div>
                    <div className="popover-employee-column-element">
                      <div className="popover-employee-column-element-icon">
                        <img src="/icons/office.svg" alt="Office Icon" />
                      </div>
                      <div className="popover-employee-element-info">
                        <div className="popover-employee-element-info-title">
                          Office
                        </div>
                        <div className="popover-employee-element-info-field">
                          {hasLoadedData ? (
                            displayProfile?.office || (
                              <i className="unknown-value">unknown</i>
                            )
                          ) : (
                            <i className="loading-value">loading...</i>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="popover-employee-column">
                    <div className="popover-employee-column-title">
                      More Details
                    </div>
                    <div className="popover-employee-row">
                      <div className="popover-employee-label">Start Date:</div>
                      <div className="popover-employee-value">
                        {hasLoadedData ? (
                          displayProfile?.startDate ? (
                            formatDate3(
                              new Date(displayProfile.startDate),
                              false
                            )
                          ) : (
                            <i className="unknown-value">unknown</i>
                          )
                        ) : (
                          <i className="loading-value">loading...</i>
                        )}
                      </div>
                    </div>
                    <div className="popover-employee-row">
                      <div className="popover-employee-label">Department:</div>
                      <div className="popover-employee-value">
                        {hasLoadedData ? (
                          displayProfile?.department || (
                            <i className="unknown-value">unknown</i>
                          )
                        ) : (
                          <i className="loading-value">loading...</i>
                        )}
                      </div>
                    </div>
                    <div className="popover-employee-row">
                      <div className="popover-employee-label">
                        Manager Name:
                      </div>
                      <div className="popover-employee-value">
                        {hasLoadedData ? (
                          displayProfile?.managerName || (
                            <i className="unknown-value">unknown</i>
                          )
                        ) : (
                          <i className="loading-value">loading...</i>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </>
            )}
          </div>
        </Popover>
      </div>

      <ModalHRISRequired
        isOpen={showHRISModal}
        onClose={() => setShowHRISModal(false)}
        onContinue={() => setShowHRISModal(false)}
      />
    </>
  );
};

export default PopoverEmployeeProfile;
